import type { JobInfo } from "@/lib/api";

/** Minimum horizontal travel (px) before a touch drag counts as a swipe. */
export const SWIPE_MIN_DISTANCE = 50;
/** Horizontal travel must dominate vertical travel by this factor. */
export const SWIPE_DOMINANCE = 1.5;

export type ViewerAction = "prev" | "next" | "fullscreen";

/** Generations the detail viewer can step through: finished with a previewable output. */
export function viewerItems<T extends Pick<JobInfo, "state" | "result">>(jobs: readonly T[]): T[] {
  return jobs.filter((job) => job.state === "completed" && Boolean(job.result.assets?.[0]?.storage_key));
}

/** Index of the neighbour `delta` steps from `id`, or -1 when there is none. */
export function neighborIndex(items: readonly { id: string }[], id: string | null, delta: number): number {
  if (!id) return -1;
  const index = items.findIndex((item) => item.id === id);
  if (index < 0) return -1;
  const target = index + delta;
  return target >= 0 && target < items.length ? target : -1;
}

interface KeyLike {
  key: string;
  defaultPrevented?: boolean;
  shiftKey?: boolean;
  ctrlKey?: boolean;
  metaKey?: boolean;
  altKey?: boolean;
  target?: unknown;
}

function isEditable(target: unknown): boolean {
  if (!target || typeof target !== "object") return false;
  const element = target as { tagName?: string; isContentEditable?: boolean };
  const tag = element.tagName?.toUpperCase();
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || Boolean(element.isContentEditable);
}

/**
 * Viewer shortcut for a keydown, or null. Keys already handled (e.g. Shift+arrow
 * seeking inside the video player), modified keys and keys typed into form
 * controls — including the seek/volume sliders — are left alone.
 */
export function viewerKeyAction(event: KeyLike): ViewerAction | null {
  if (event.defaultPrevented) return null;
  if (event.shiftKey || event.ctrlKey || event.metaKey || event.altKey) return null;
  if (isEditable(event.target)) return null;
  switch (event.key) {
    case "ArrowLeft":
      return "prev";
    case "ArrowRight":
      return "next";
    case "f":
    case "F":
      return "fullscreen";
    default:
      return null;
  }
}

/** Direction of a finished drag: swiping left reveals the next item. */
export function swipeDirection(dx: number, dy: number): "prev" | "next" | null {
  if (Math.abs(dx) < SWIPE_MIN_DISTANCE) return null;
  if (Math.abs(dx) < Math.abs(dy) * SWIPE_DOMINANCE) return null;
  return dx < 0 ? "next" : "prev";
}

/** "3 / 48", with a plus while more pages can still load. */
export function positionLabel(index: number, total: number, hasMore = false): string {
  return `${index + 1} / ${total}${hasMore ? "+" : ""}`;
}
